import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import api, { formatApiError, API_BASE } from "@/lib/api";

const toAbs = (url) => {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${API_BASE}${url.slice(4)}`;
  return url;
};

/** Upload field for an audio or video file from the local device, with progress. */
export default function MediaUploadField({ value, onChange, kind = "audio", label, testIdPrefix = "media" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const endpoint = kind === "audio" ? "/uploads/audio" : "/uploads/video";
  const accept = kind === "audio" ? "audio/*" : "video/*";
  const heading = label || (kind === "audio" ? "Audio file" : "Video file");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(endpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setProgress(Math.round((p.loaded * 100) / (p.total || 1))),
      });
      onChange(data.url);
      toast.success(`${heading} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-field`}>
      <label className="gv-label">{heading}</label>
      <input ref={fileRef} type="file" accept={accept} onChange={handleFile} className="hidden" data-testid={`${testIdPrefix}-input`} />
      {value ? (
        <div className="mt-2 border border-[#E5E5E5] p-3 bg-[#FAFAF8]">
          {kind === "audio" ? (
            <audio controls src={toAbs(value)} className="w-full" data-testid={`${testIdPrefix}-preview`} />
          ) : (
            <video controls src={toAbs(value)} className="w-full max-h-64" data-testid={`${testIdPrefix}-preview`} />
          )}
          <div className="mt-2 flex gap-3">
            <button type="button" onClick={() => fileRef.current?.click()} className="text-xs tracking-[0.18em] uppercase text-[#C5A059] gv-link" data-testid={`${testIdPrefix}-replace`}>Replace</button>
            <button type="button" onClick={() => onChange("")} className="text-xs tracking-[0.18em] uppercase text-[#5C6670] hover:text-red-500 inline-flex items-center gap-1" data-testid={`${testIdPrefix}-remove`}><X className="w-3 h-3" /> Remove</button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="mt-2 w-full border border-dashed border-[#C5A059]/50 bg-[#FBF7EE] py-6 flex flex-col items-center gap-2 text-[#5C6670] hover:border-[#C5A059] transition-colors"
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          <Upload className="w-5 h-5 text-[#C5A059]" />
          <span className="text-sm">{uploading ? `Uploading… ${progress}%` : `Upload ${kind} from your device`}</span>
        </button>
      )}
      {uploading && (
        <div className="mt-2 h-1 bg-[#E5E5E5] overflow-hidden">
          <div className="h-full bg-[#C5A059] transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}
